import { useState, useEffect } from "react";
import { Image, X } from "lucide-react"; // Import X icon for delete
import { Card } from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Textarea } from "../../components/ui/textarea";
import { Button } from "../../components/ui/button";
import {
  createOurStory,
  getAllOurStories,
  updateOurStory,
} from "../../services/api"; // Import API functions
import { toast, ToastContainer } from "react-toastify"; // Import react-toastify
import "react-toastify/dist/ReactToastify.css"; // Import the CSS

interface OurStoryData {
  id: string;
  media_url: string;
  content: string;
  created_at: string;
  updated_at: string;
}

const OurStory = () => {
  const [story, setStory] = useState("");
  const [file, setFile] = useState<File | null>(null);
  const [previewUrl, setPreviewUrl] = useState<string | null>(null); // For file preview
  const [isEditing, setIsEditing] = useState(false); // Toggle between view and edit modes
  const [existingStory, setExistingStory] = useState<OurStoryData | null>(null) as any; // Store existing story data
  const [isLoading, setIsLoading] = useState(true);

  // Fetch existing story data on component mount
  useEffect(() => {
    fetchOurStory();
  }, []);

  const fetchOurStory = async () => {
    try {
      const response = (await getAllOurStories()) as any;
      if (response.data.data && response.data.data.length > 0) {
        // console.log({response});

        const storyData = response.data.data[0]; // Get the first story (assuming only one exists)
        setExistingStory(storyData); // Set existing story data
        setStory(storyData.content); // Prefill the story content
        setPreviewUrl(storyData.media_url); // Prefill the image/video preview
      }
    } catch (error) {
      toast.error("Failed to fetch story.", {
        position: "top-center",
        autoClose: 3000,
      });
    }finally {
      setIsLoading(false);
    }
  };

  const handleFileChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      const selectedFile = event.target.files[0];
      setFile(selectedFile);

      // Generate a preview URL for the selected file
      const fileUrl = URL.createObjectURL(selectedFile);
      setPreviewUrl(fileUrl);
    }
  };

  const handleDeleteFile = () => {
    setFile(null);
    setPreviewUrl(null);

    // Clear the file input value
    const fileInput = document.getElementById("fileInput") as HTMLInputElement;
    if (fileInput) {
      fileInput.value = "";
    }
  };

  const handleSave = async () => {
    if (!story) {
      toast.error("Please fill in the story content.", {
        position: "top-center",
        autoClose: 3000,
      });
      return;
    }

    const formData = new FormData() as any;
    formData.append("content", story);
    if (file) {
      formData.append("image", file);
    }

    try {
      if (existingStory) {
        // Update existing story
        const response = await updateOurStory(existingStory.id, formData);
        toast.success("Story updated successfully!", {
          position: "top-center",
          autoClose: 3000,
        });
      } else {
        // Create new story
        const response = await createOurStory(formData);
        toast.success("Story created successfully!", {
          position: "top-center",
          autoClose: 3000,
        });
      }

      // Refetch story data after saving
      await fetchOurStory();
      setIsEditing(false); // Switch back to view mode
    } catch (error) {
      toast.error("Failed to save story.", {
        position: "top-center",
        autoClose: 3000,
      });
    }
  };

  return (
    <div className="p-8">
      {/* Toast Container */}
      <ToastContainer />

      <div className="mb-8">
        <h1 className="text-2xl font-semibold mb-1">Our Story</h1>
        <p className="text-muted-foreground">
          The journey behind Charmé legacy
        </p>
      </div>

      <Card>
        <div className="p-6">
          {isLoading ? (
            <div className="flex justify-center items-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-gray-900"></div>
            </div>
          ) : (
            <div className="grid md:grid-cols-[300px,1fr] gap-8">
              <div>
                <Label className="mb-2 inline-block">About Charmé</Label>
                <div
                  className="border-2 border-dashed rounded-lg aspect-square flex flex-col items-center justify-center cursor-pointer hover:border-[#B87775] transition-colors relative"
                  onClick={() =>
                    isEditing && document.getElementById("fileInput")?.click()
                  }
                >
                  {previewUrl ? (
                    <>
                      {/* Preview Image/Video */}
                      {file?.type?.startsWith("image") ||
                      (existingStory?.media_url &&
                        (existingStory.media_url.endsWith(".jpg")) || existingStory.media_url.endsWith(".png") || existingStory.media_url.endsWith(".svg")) ? (
                        <img
                          src={previewUrl || existingStory?.media_url}
                          alt="Preview"
                          className="w-full h-full object-cover rounded-lg"
                        />
                      ) : file?.type?.startsWith("video") ||
                        (existingStory?.media_url &&
                          (existingStory.media_url.endsWith(".mov") ||
                            existingStory.media_url.endsWith(".mp4"))) ? (
                        <video
                          src={previewUrl || existingStory?.media_url}
                          className="w-full h-full object-cover rounded-lg"
                          controls
                        />
                      ) : null}

                      {/* Delete Button (only in edit mode) */}
                      {isEditing && (
                        <button
                          className="absolute top-2 right-2 p-1 bg-white rounded-full shadow-sm hover:bg-gray-100 transition-colors"
                          onClick={(e) => {
                            e.stopPropagation(); // Prevent triggering the file input
                            handleDeleteFile();
                          }}
                        >
                          <X className="h-4 w-4 text-gray-700" />
                        </button>
                      )}
                    </>
                  ) : (
                    <div className="text-center">
                      <Image className="h-10 w-10 mx-auto mb-2 text-muted-foreground" />
                      <div className="text-sm text-muted-foreground">
                        UPLOAD IMAGE OR VIDEO
                      </div>
                    </div>
                  )}
                  <input
                    id="fileInput"
                    type="file"
                    style={{ display: "none" }}
                    onChange={handleFileChange}
                    accept="image/*, video/*" // Allow only images and videos
                    disabled={!isEditing} // Disable file input in view mode
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <Label htmlFor="story" className="text-red-500">
                    *
                  </Label>
                  <Textarea
                    id="story"
                    placeholder="Let's share how far we have come..."
                    className="min-h-[300px]"
                    value={story}
                    onChange={(e) => setStory(e.target.value)}
                    disabled={!isEditing} // Disable textarea in view mode
                  />
                </div>

                <div className="flex justify-end gap-2">
                  {isEditing ? (
                    <>
                      <Button
                        variant="outline"
                        onClick={() => {
                          setIsEditing(false); // Cancel edit mode
                          fetchOurStory(); // Reset to existing data
                        }}
                      >
                        CANCEL
                      </Button>
                      <Button
                        className="bg-[#B87775] hover:bg-[#B87775]/90 text-white"
                        onClick={handleSave}
                      >
                        SAVE
                      </Button>
                    </>
                  ) : (
                    <Button
                      className="bg-[#B87775] hover:bg-[#B87775]/90 text-white"
                      onClick={() => setIsEditing(true)}
                    >
                      EDIT
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default OurStory;
